import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Package as PackageIcon, Search, RefreshCw, Calendar,
  ChevronDown, ChevronUp, AlertCircle, History as HistoryIcon, X as XIcon,
  IndianRupee, TrendingDown, TrendingUp, Download, XCircle, Lock,
} from 'lucide-react';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'closed', label: 'Closed' },
];
const SORT_OPTIONS = [
  { key: 'created_desc', label: 'Newest first' },
  { key: 'created_asc', label: 'Oldest first' },
  { key: 'balance_desc', label: 'Highest balance' },
  { key: 'expiry_asc', label: 'Expiring soon' },
];
const PAGE_LIMIT = 25;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');
const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d - Date.now()) / (24 * 60 * 60 * 1000));
};

const StatusPill = ({ status }) => {
  const key = String(status || '').toLowerCase();
  const cls = {
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-rose-100 text-rose-700',
    closed: 'bg-gray-200 text-gray-700',
  }[key] || 'bg-gray-100 text-gray-700';
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : '—';
  return <Badge className={`${cls} text-[10px] capitalize`}>{label}</Badge>;
};

const StatCard = ({ icon: Icon, label, value, hint, tone = 'purple', testid }) => {
  const tones = {
    purple: 'from-purple-500 to-purple-600 text-white',
    orange: 'from-orange-400 to-orange-500 text-white',
    blue: 'from-blue-500 to-blue-600 text-white',
    emerald: 'from-emerald-500 to-emerald-600 text-white',
  };
  return (
    <div className={`bg-gradient-to-br ${tones[tone]} rounded-2xl p-4 shadow-sm`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs opacity-80">
        <Icon className="w-4 h-4" />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {hint && <p className="text-[11px] opacity-80 mt-0.5">{hint}</p>}
    </div>
  );
};

const PackagesPage = ({ user, onLogout }) => {
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState('active');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('created_desc');
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Cancel confirmation
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Close confirmation
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  const load = useCallback(async ({ page: p = 0 } = {}) => {
    try {
      setLoading(true);
      const params = { limit: PAGE_LIMIT, skip: p * PAGE_LIMIT };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const res = await api.get('/packages', { params });
      setData(res.data || { total: 0, rows: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query]);

  useEffect(() => { setPage(0); load({ page: 0 }); }, [statusFilter, load]);

  // Deep-link support: /packages?ref=PKG-XXXX  → auto-search + expand
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const patient = params.get('patient_id');
    if (ref) {
      setStatusFilter('all');
      setQuery(ref);
      (async () => {
        try {
          const res = await api.get('/packages', { params: { q: ref, limit: 5 } });
          const rows = res.data?.rows || [];
          setData(res.data || { total: 0, rows: [] });
          const match = rows.find(r => r.ref_number === ref) || rows[0];
          if (match) {
            setExpandedId(match.id);
            const r = await api.get(`/packages/${match.id}`);
            setDetail(d => ({ ...d, [match.id]: r.data }));
          }
        } catch { /* silent */ }
      })();
    } else if (patient) {
      setStatusFilter('all');
      // We don't have a patient_id search, so open by patient name would need
      // extra plumbing — leave as no-op for now.
    }
  }, [location.search]);

  const toggleExpand = async (pkg) => {
    if (expandedId === pkg.id) { setExpandedId(null); return; }
    setExpandedId(pkg.id);
    if (!detail[pkg.id]) {
      setDetailLoading(pkg.id);
      try {
        const r = await api.get(`/packages/${pkg.id}`);
        setDetail(d => ({ ...d, [pkg.id]: r.data }));
      } catch (e) {
        toast.error('Failed to load details');
      } finally {
        setDetailLoading(null);
      }
    }
  };

  const openHistory = async (pkg, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setHistoryTarget(pkg);
    setHistoryOpen(true);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/packages/${pkg.id}/versions`);
      setHistoryRows(res.data?.versions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    try {
      setCancelSubmitting(true);
      await api.post(`/packages/${cancelTarget.id}/cancel`, {});
      toast.success('Package cancelled');
      setCancelTarget(null);
      setCancelReason('');
      load({ page });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const submitClose = async () => {
    if (!closeTarget) return;
    try {
      setCloseSubmitting(true);
      await api.post(`/packages/${closeTarget.id}/close`, {});
      toast.success('Package closed');
      setCloseTarget(null);
      load({ page });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close');
    } finally {
      setCloseSubmitting(false);
    }
  };

  // Derived rows (sort + expiring filter)
  const displayRows = useMemo(() => {
    let out = [...(data.rows || [])];
    if (expiringSoon) {
      out = out.filter(p => {
        const d = daysUntil(p.expiry_date);
        return d !== null && d >= 0 && d <= 30;
      });
    }
    const cmp = {
      created_desc: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
      created_asc: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
      balance_desc: (a, b) => Number(b.balance || 0) - Number(a.balance || 0),
      expiry_asc: (a, b) => {
        const av = a.expiry_date || '9999-12-31';
        const bv = b.expiry_date || '9999-12-31';
        return String(av).localeCompare(String(bv));
      },
    }[sortKey];
    if (cmp) out.sort(cmp);
    return out;
  }, [data.rows, sortKey, expiringSoon]);

  // Hero stats across all rows shown (page-scoped, not global — good enough)
  const stats = useMemo(() => {
    const active = (data.rows || []).filter(p => p.status === 'active');
    const totalActive = active.length;
    const totalCreditsLeft = active.reduce((s, p) => s + (p.items || []).reduce((t, it) => t + (Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)), 0), 0);
    const totalBalance = active.reduce((s, p) => s + Number(p.balance || 0), 0);
    const totalCollected = active.reduce((s, p) => s + Number(p.total_paid || 0), 0);
    return { totalActive, totalCreditsLeft, totalBalance, totalCollected };
  }, [data.rows]);

  const exportCSV = () => {
    const rows = displayRows;
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    const headers = ['ref_number', 'patient_name', 'patient_phone', 'status', 'created_at', 'expiry_date',
                     'total_cost', 'total_paid', 'balance', 'items'];
    const lines = [headers.join(',')];
    rows.forEach(p => {
      const items = (p.items || []).map(it => `${it.service_name} ${it.quantity_consumed || 0}/${it.quantity_total}`).join(' | ');
      const vals = [
        p.ref_number, p.patient_name, p.patient_phone, p.status,
        String(p.created_at || '').slice(0, 10),
        String(p.expiry_date || ''),
        p.total_cost, p.total_paid, p.balance,
        items,
      ].map(v => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      });
      lines.push(vals.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packages-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const total = data.total || 0;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_LIMIT < total;
  const canWrite = user?.role === 'Admin' || user?.role === 'Teller';
  const isAdmin = user?.role === 'Admin';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-xl">
              <PackageIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Packages</h1>
              <p className="text-sm text-gray-500">Pre-paid service bundles across all patients</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCSV}
              disabled={displayRows.length === 0}
              data-testid="packages-export"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ page })}
              disabled={loading}
              data-testid="packages-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={PackageIcon} label="Active packages" value={stats.totalActive} tone="purple" testid="stat-active-count" />
          <StatCard icon={TrendingDown} label="Credits outstanding" value={stats.totalCreditsLeft} tone="blue" testid="stat-credits-left" hint="unconsumed sessions" />
          <StatCard icon={IndianRupee} label="Balance owed" value={fmtINR(stats.totalBalance)} tone="orange" testid="stat-balance-owed" hint="from active packages" />
          <StatCard icon={TrendingUp} label="Collected" value={fmtINR(stats.totalCollected)} tone="emerald" testid="stat-collected" hint="on active packages" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {STATUS_FILTERS.map(s => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                data-testid={`packages-filter-${s.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  statusFilter === s.key
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={expiringSoon}
                  onChange={(e) => setExpiringSoon(e.target.checked)}
                  data-testid="packages-filter-expiring"
                  className="accent-orange-500"
                />
                Expiring within 30 days
              </label>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                data-testid="packages-sort"
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white hover:border-gray-300"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by ref number, patient name, or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); load({ page: 0 }); } }}
              className="pl-9"
              data-testid="packages-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {loading && (
            <div className="p-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && displayRows.length === 0 && (
            <div className="p-10 text-center text-sm text-gray-500" data-testid="packages-empty">
              <PackageIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              No packages match your filters.
            </div>
          )}
          {!loading && displayRows.map(pkg => {
            const remaining = (pkg.items || []).reduce((s, it) => s + (Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)), 0);
            const totalQty = (pkg.items || []).reduce((s, it) => s + Number(it.quantity_total || 0), 0);
            const isExpanded = expandedId === pkg.id;
            const days = daysUntil(pkg.expiry_date);
            const isExpiringSoon = days !== null && days >= 0 && days <= 30;
            return (
              <div key={pkg.id} className="border-b border-gray-100 last:border-b-0" data-testid={`packages-row-${pkg.ref_number}`}>
                <div
                  onClick={() => toggleExpand(pkg)}
                  className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded">{pkg.ref_number}</span>
                      <StatusPill status={pkg.status} />
                      {pkg.expiry_date && (
                        <span className={`text-[11px] flex items-center gap-0.5 ${isExpiringSoon ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                          <Calendar className="w-3 h-3" />
                          exp {fmtDate(pkg.expiry_date)}
                          {isExpiringSoon && ` · ${days}d`}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900 truncate">{pkg.patient_name}</p>
                    <p className="text-xs text-gray-500">{pkg.patient_phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{fmtINR(pkg.total_cost)}</p>
                    <p className="text-[11px] text-gray-500">
                      Paid {fmtINR(pkg.total_paid)} · Bal <span className={Number(pkg.balance) > 0 ? 'text-orange-600 font-medium' : ''}>{fmtINR(pkg.balance)}</span>
                    </p>
                    <p className="text-[11px] text-purple-700 mt-0.5">{remaining}/{totalQty} credits left</p>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>

                {isExpanded && (
                  <div className="bg-gray-50 border-t border-gray-100 px-5 py-4">
                    {/* Services */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Services</p>
                      <div className="space-y-1">
                        {(pkg.items || []).map((it, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{it.service_name}</span>
                            <span className="text-gray-500 font-mono text-xs">
                              {it.quantity_consumed || 0}/{it.quantity_total}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Ledger */}
                    {detailLoading === pkg.id && (
                      <div className="text-xs text-gray-500 italic">Loading ledger…</div>
                    )}
                    {detail[pkg.id] && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">Payments</p>
                          {(detail[pkg.id].payments || []).length === 0 && (
                            <p className="text-xs text-gray-400 italic">No payments yet.</p>
                          )}
                          <div className="space-y-1">
                            {(detail[pkg.id].payments || []).map(p => (
                              <div key={p.id} className={`text-xs flex items-center justify-between border rounded px-2 py-1 ${p.status === 'cancelled' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`} data-testid={`pkg-payment-${p.id}`}>
                                <span className={`truncate pr-2 ${p.status === 'cancelled' ? 'text-red-700 line-through' : 'text-gray-600'}`}>
                                  {String(p.payment_date || p.created_at || '').slice(0, 10)} · {p.payment_method_name}
                                  {p.package_payment_kind ? ` · ${p.package_payment_kind}` : ''}
                                  {p.receipt_number ? ` · ${p.receipt_number}` : ''}
                                </span>
                                <span className={`font-semibold ${p.status === 'cancelled' ? 'text-red-700' : 'text-gray-900'}`}>
                                  {p.status === 'cancelled' && <span className="mr-1 text-[10px] uppercase tracking-wide no-underline">cancelled</span>}
                                  {fmtINR(p.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">Consumptions</p>
                          {(detail[pkg.id].consumptions || []).length === 0 && (
                            <p className="text-xs text-gray-400 italic">No credits consumed yet.</p>
                          )}
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {(detail[pkg.id].consumptions || []).map(c => (
                              <div key={c.id} className="text-xs flex items-center justify-between bg-white border border-gray-200 rounded px-2 py-1">
                                <span className="text-gray-600 truncate pr-2">{String(c.consumed_at || c.created_at || '').slice(0, 10)} · {c.service_name}</span>
                                <span className="text-gray-500">{c.consumed_by_user_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {pkg.notes && (
                      <div className="mt-3 text-xs text-gray-600 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5 text-gray-400" /> {pkg.notes}
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                      {pkg.last_edited_by_user_name && (
                        <span className="text-[11px] text-gray-500 italic">
                          Last edited by {pkg.last_edited_by_user_name} · {String(pkg.last_edited_at || '').slice(0, 16).replace('T', ' ')}
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => openHistory(pkg, e)}
                          data-testid={`packages-history-${pkg.ref_number}`}
                          className="h-7 text-xs"
                        >
                          <HistoryIcon className="w-3 h-3 mr-1" />
                          History
                        </Button>
                        {canWrite && pkg.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCloseTarget(pkg)}
                            data-testid={`packages-close-${pkg.ref_number}`}
                            className="h-7 text-xs border-gray-300 text-gray-700 hover:bg-gray-100"
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            Close
                          </Button>
                        )}
                        {isAdmin && pkg.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCancelTarget(pkg)}
                            data-testid={`packages-cancel-${pkg.ref_number}`}
                            className="h-7 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {(hasPrev || hasNext) && (
            <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
              <span className="text-xs text-gray-500">
                Showing {page * PAGE_LIMIT + 1}–{page * PAGE_LIMIT + displayRows.length} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasPrev || loading}
                  onClick={() => { const np = Math.max(0, page - 1); setPage(np); load({ page: np }); }}
                  data-testid="packages-prev-page"
                >Previous</Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasNext || loading}
                  onClick={() => { const np = page + 1; setPage(np); load({ page: np }); }}
                  data-testid="packages-next-page"
                >Next</Button>
              </div>
            </div>
          )}
        </div>

        {/* Version History Modal */}
        {historyOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => { setHistoryOpen(false); setHistoryTarget(null); }}
            data-testid="packages-history-modal"
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex items-center gap-2">
                  <HistoryIcon className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Version history</p>
                    <p className="text-xs text-gray-500 font-mono">{historyTarget?.ref_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setHistoryOpen(false); setHistoryTarget(null); }}
                  className="p-1.5 hover:bg-gray-100 rounded-md"
                  data-testid="packages-history-close"
                >
                  <XIcon className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <div className="p-5">
                {historyLoading && (
                  <div className="text-center py-8 text-sm text-gray-500 flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Loading history…
                  </div>
                )}
                {!historyLoading && historyRows.length === 0 && (
                  <div className="text-center py-10 text-sm text-gray-500" data-testid="packages-history-empty">
                    <HistoryIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No edits recorded for this package yet.
                  </div>
                )}
                {!historyLoading && historyRows.length > 0 && (
                  <div className="space-y-3">
                    {historyRows.map((v, idx) => (
                      <div key={v.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50/50" data-testid={`packages-history-row-${idx}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-xs text-gray-500">
                              {String(v.snapshot_at || '').slice(0, 19).replace('T', ' ')}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {v.edited_by_user_name || 'Unknown'}{' '}
                              <span className="text-xs text-gray-500 font-normal">({v.edited_by_role || '—'})</span>
                            </p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700 text-[10px]">v{historyRows.length - idx}</Badge>
                        </div>
                        {v.reason && (
                          <p className="text-xs italic text-gray-600 mb-3">&ldquo;{v.reason}&rdquo;</p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="bg-orange-50/60 border border-orange-100 rounded-lg p-3">
                            <p className="font-semibold text-orange-700 mb-1">Before</p>
                            <p className="text-gray-700">Total cost: {fmtINR(v.previous?.total_cost)}</p>
                            <p className="text-gray-700">Paid: {fmtINR(v.previous?.total_paid)}</p>
                            <p className="text-gray-700">Expiry: {fmtDate(v.previous?.expiry_date)}</p>
                            <div className="mt-2 space-y-0.5">
                              {(v.previous?.items || []).map((it, i) => (
                                <div key={i} className="flex justify-between">
                                  <span className="truncate pr-1 text-gray-600">{it.service_name}</span>
                                  <span className="text-gray-700">{it.quantity_consumed || 0}/{it.quantity_total}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
                            <p className="font-semibold text-emerald-700 mb-1">After</p>
                            <p className="text-gray-700">Total cost: {fmtINR(v.new?.total_cost)}</p>
                            <p className="text-gray-400">&nbsp;</p>
                            <p className="text-gray-700">Expiry: {fmtDate(v.new?.expiry_date)}</p>
                            <div className="mt-2 space-y-0.5">
                              {(v.new?.items || []).map((it, i) => (
                                <div key={i} className="flex justify-between">
                                  <span className="truncate pr-1 text-gray-600">{it.service_name}</span>
                                  <span className="text-gray-700">x{it.quantity_total}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancel confirmation */}
        <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(''); } }}>
          <DialogContent className="max-w-sm" data-testid="packages-cancel-dialog">
            <DialogHeader>
              <DialogTitle className="text-rose-700">Cancel package {cancelTarget?.ref_number}?</DialogTitle>
              <DialogDescription>
                This marks the package as <b>cancelled</b>. Consumed sessions and payments already recorded are preserved.
                {Number(cancelTarget?.total_paid || 0) > 0 && (
                  <span className="block mt-2 text-orange-700 text-xs">
                    Patient has already paid {fmtINR(cancelTarget?.total_paid)} — refunds must be handled separately in the POS.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <label className="text-xs text-gray-500">Reason (optional, for the audit log)</label>
              <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="packages-cancel-reason" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelTarget(null)}>Back</Button>
              <Button
                onClick={submitCancel}
                disabled={cancelSubmitting}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="packages-cancel-confirm"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {cancelSubmitting ? 'Cancelling…' : 'Yes, cancel'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Close confirmation */}
        <Dialog open={!!closeTarget} onOpenChange={(o) => { if (!o) setCloseTarget(null); }}>
          <DialogContent className="max-w-sm" data-testid="packages-close-dialog">
            <DialogHeader>
              <DialogTitle>Close package {closeTarget?.ref_number}?</DialogTitle>
              <DialogDescription>
                This marks the package as <b>closed</b> (locked). Use this when the patient has fully consumed or the package is done. Cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseTarget(null)}>Back</Button>
              <Button
                onClick={submitClose}
                disabled={closeSubmitting}
                className="bg-gray-800 hover:bg-gray-900"
                data-testid="packages-close-confirm"
              >
                <Lock className="w-4 h-4 mr-2" />
                {closeSubmitting ? 'Closing…' : 'Yes, close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PackagesPage;
